import React from "react";
import { Tooltip } from "react-tooltip";
import { useOutletContext } from "react-router-dom";

import {
  IDemoPersistedRoiRequest,
  mapDemoStoredRequestToDetails,
  saveDemoRoiRequest,
} from "../model/roi-request-create.storage";
import { useRoiRequestCreate } from "../model/use-roi-request-create";
import * as Styled from "./roi-request-create-flow.styled";
import * as SelectorStyled from "./roi-request-create-selector.styled";
import { RoiRequestCreateSectionTabs } from "./roi-request-create-section-tabs";
import { RoiRequestCreateSelectorTable } from "./roi-request-create-selector-table";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const RoiRequestCreateTriggerButton = Styled.TablePrimaryActionButton;

export const RoiRequestCreateFlow = ({
  isOpen,
  mode = "create",
  request = null,
  onExitToList,
}: {
  isOpen: boolean;
  mode?: "create" | "edit";
  request?: IDemoPersistedRoiRequest | null;
  onExitToList: () => void;
}) => {
  const { setIsStretchContent } = useOutletContext<IPatientInfoOutletContext>();
  const isEditMode = mode === "edit";
  const disabledFooterTooltip = "Select at least 1 record to proceed";
  const [isRequestDetailsExpanded, setIsRequestDetailsExpanded] =
    React.useState(false);
  const {
    sections,
    currentStep,
    activeSectionId,
    requestIdInput,
    searchTerm,
    dateRangeFromInput,
    dateRangeToInput,
    dateRangeFromValidationMessage,
    dateRangeToValidationMessage,
    requestState,
    openFlow,
    exitFlow,
    goBack,
    continueFromRequestId,
    setRequestId,
    setSearchTerm,
    setDateRangeFrom,
    setDateRangeTo,
    clearDateRangeFrom,
    clearDateRangeTo,
    markDateRangeFromTouched,
    markDateRangeToTouched,
    setActiveSection,
    toggleRowSelection,
    toggleAllRowsInSection,
    setRequestStateDraft,
    setRequestStateCompleted,
    getSectionSelection,
    isCompleteAllowed,
    isSaveDraftAllowed,
    getActiveSection,
  } = useRoiRequestCreate({
    mode,
    initialRequestState: request,
  });
  const activeSection = getActiveSection();
  const requestDetails = React.useMemo(
    () => (request ? mapDemoStoredRequestToDetails(request) : null),
    [request],
  );

  React.useEffect(() => {
    if (!isOpen) {
      exitFlow();
    }
  }, [exitFlow, isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      openFlow();
    }
  }, [isOpen, openFlow]);

  React.useEffect(() => {
    setIsStretchContent(isOpen);

    return () => {
      setIsStretchContent(false);
    };
  }, [isOpen, setIsStretchContent]);

  React.useEffect(() => {
    if (!isOpen) {
      setIsRequestDetailsExpanded(false);
    }
  }, [isOpen]);

  const handleBack = () => {
    if (!isEditMode && currentStep === "create-details") {
      goBack();
      return;
    }

    onExitToList();
  };

  const handleExit = () => {
    exitFlow();
    onExitToList();
  };

  const handleContinue = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    continueFromRequestId();
  };

  const handleSaveAsDraft = () => {
    const payload = setRequestStateDraft();
    saveDemoRoiRequest(payload, { baseRequest: request ?? undefined });
    handleExit();
  };

  const handleCompleteRequest = () => {
    const payload = setRequestStateCompleted();
    saveDemoRoiRequest(payload, { baseRequest: request ?? undefined });
    handleExit();
  };

  if (currentStep === "create-id") {
    return (
      <Styled.Container>
        <Styled.FormScreen>
          <Styled.BackButton type="button" onClick={handleBack}>
            <i className="pi pi-arrow-left" aria-hidden="true" />
            <span>Back</span>
          </Styled.BackButton>
          <Styled.FormHeader>
            <Styled.Title>Create ROI Request</Styled.Title>
            <Styled.Subtitle>
              Begin an ROI request by filling out the Request ID
            </Styled.Subtitle>
          </Styled.FormHeader>
          <Styled.Form onSubmit={handleContinue}>
            <Styled.Fieldset>
              <Styled.Label htmlFor="request-id">Request ID</Styled.Label>
              <Styled.ControlsRow>
                <Styled.RequestIdInput
                  id="request-id"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={requestIdInput}
                  onChange={(event) => setRequestId(event.target.value)}
                />
                <Styled.ContinueButton
                  type="submit"
                  disabled={requestIdInput.length === 0}
                >
                  <span>Continue</span>
                  <i className="pi pi-arrow-right" aria-hidden="true" />
                </Styled.ContinueButton>
              </Styled.ControlsRow>
            </Styled.Fieldset>
          </Styled.Form>
        </Styled.FormScreen>
      </Styled.Container>
    );
  }

  const requestMetaItems = requestDetails
    ? [
        { label: "MRN", value: requestDetails.mrn },
        { label: "LDA ID", value: requestDetails.ldaId },
        { label: "Request ID", value: requestDetails.requestId },
        { label: "Created By", value: requestDetails.createdBy },
        { label: "Date Created", value: requestDetails.dateCreated },
        { label: "Last Updated", value: requestDetails.lastUpdated },
      ]
    : [];

  return (
    <Styled.StepScreen>
      <Styled.StepHeader>
        <Styled.BackButton type="button" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>Back</span>
        </Styled.BackButton>
        {isEditMode ? (
          <Styled.HeaderTop $expanded={isRequestDetailsExpanded}>
            <Styled.TitleRow>
              <Styled.Title>Edit ROI Request</Styled.Title>
              <Styled.DraftBadge>Draft</Styled.DraftBadge>
              {requestDetails ? (
                <Styled.RequestDetailsButton
                  type="button"
                  $expanded={isRequestDetailsExpanded}
                  aria-expanded={isRequestDetailsExpanded}
                  onClick={() =>
                    setIsRequestDetailsExpanded(
                      (previousValue) => !previousValue,
                    )
                  }
                >
                  <span>Request Details</span>
                  <i className="pi pi-angle-down" aria-hidden="true" />
                </Styled.RequestDetailsButton>
              ) : null}
            </Styled.TitleRow>
            {requestDetails ? (
              <Styled.RequestDetailsPanel $expanded={isRequestDetailsExpanded}>
                <Styled.RequestDetailsPanelInner>
                  <Styled.RequestMetaGrid>
                    {requestMetaItems.map((item) => (
                      <Styled.RequestMetaItem key={item.label}>
                        <Styled.RequestMetaLabel>
                          {item.label}
                        </Styled.RequestMetaLabel>
                        <Styled.RequestMetaValue>
                          {item.value}
                        </Styled.RequestMetaValue>
                      </Styled.RequestMetaItem>
                    ))}
                  </Styled.RequestMetaGrid>
                </Styled.RequestDetailsPanelInner>
              </Styled.RequestDetailsPanel>
            ) : null}
          </Styled.HeaderTop>
        ) : null}
        <Styled.SelectionHeader>
          <Styled.SelectionHeaderText>
            {!isEditMode ? (
              <Styled.Title>Create ROI Request</Styled.Title>
            ) : null}
            <Styled.Subtitle>
              Select the records you want to include in the ROI request
            </Styled.Subtitle>
          </Styled.SelectionHeaderText>
          <Styled.DateRangeFilter>
            <Styled.DateRangeLabel>From</Styled.DateRangeLabel>
            <Styled.DateRangeField>
              <Styled.DateRangeInputWrapper>
                <Styled.DateRangeInput
                  $invalid={!!dateRangeFromValidationMessage}
                  $hasClearButton={dateRangeFromInput.length > 0}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="mm/dd/yyyy"
                  aria-label="From date"
                  aria-invalid={!!dateRangeFromValidationMessage}
                  aria-describedby={
                    dateRangeFromValidationMessage
                      ? "roi-date-range-from-error"
                      : undefined
                  }
                  value={dateRangeFromInput}
                  onChange={(event) => setDateRangeFrom(event.target.value)}
                  onBlur={markDateRangeFromTouched}
                />
                {dateRangeFromInput ? (
                  <Styled.DateRangeClearButton
                    type="button"
                    aria-label="Clear from date"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clearDateRangeFrom}
                  >
                    <i className="pi pi-times" aria-hidden="true" />
                  </Styled.DateRangeClearButton>
                ) : null}
              </Styled.DateRangeInputWrapper>
              {dateRangeFromValidationMessage ? (
                <Styled.DateRangeErrorText id="roi-date-range-from-error">
                  {dateRangeFromValidationMessage}
                </Styled.DateRangeErrorText>
              ) : null}
            </Styled.DateRangeField>
            <Styled.DateRangeLabel>to</Styled.DateRangeLabel>
            <Styled.DateRangeField>
              <Styled.DateRangeInputWrapper>
                <Styled.DateRangeInput
                  $invalid={!!dateRangeToValidationMessage}
                  $hasClearButton={dateRangeToInput.length > 0}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="mm/dd/yyyy"
                  aria-label="To date"
                  aria-invalid={!!dateRangeToValidationMessage}
                  aria-describedby={
                    dateRangeToValidationMessage
                      ? "roi-date-range-to-error"
                      : undefined
                  }
                  value={dateRangeToInput}
                  onChange={(event) => setDateRangeTo(event.target.value)}
                  onBlur={markDateRangeToTouched}
                />
                {dateRangeToInput ? (
                  <Styled.DateRangeClearButton
                    type="button"
                    aria-label="Clear to date"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clearDateRangeTo}
                  >
                    <i className="pi pi-times" aria-hidden="true" />
                  </Styled.DateRangeClearButton>
                ) : null}
              </Styled.DateRangeInputWrapper>
              {dateRangeToValidationMessage ? (
                <Styled.DateRangeErrorText id="roi-date-range-to-error">
                  {dateRangeToValidationMessage}
                </Styled.DateRangeErrorText>
              ) : null}
            </Styled.DateRangeField>
          </Styled.DateRangeFilter>
        </Styled.SelectionHeader>
      </Styled.StepHeader>
      <Styled.StepContent>
        <SelectorStyled.DetailsLayout>
          <SelectorStyled.SelectorPanel>
            <RoiRequestCreateSectionTabs
              sections={sections}
              activeSectionId={activeSectionId}
              onTabClick={setActiveSection}
            />
            {activeSection ? (
              <RoiRequestCreateSelectorTable
                section={activeSection}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                selectedIds={getSectionSelection(activeSection.section_id)}
                onToggleRowSelection={toggleRowSelection}
                onToggleAllRowsInSection={toggleAllRowsInSection}
              />
            ) : null}
          </SelectorStyled.SelectorPanel>
        </SelectorStyled.DetailsLayout>
      </Styled.StepContent>
      <Styled.Footer>
        <Styled.FooterMeta>
          {requestState.total_selected_items_count} records selected in total
        </Styled.FooterMeta>
        <Styled.FooterActions>
          <Styled.SecondaryActionButton type="button" onClick={handleExit}>
            Exit
          </Styled.SecondaryActionButton>
          <Styled.TooltipAnchor
            data-tooltip-id={
              isSaveDraftAllowed ? undefined : "roi-footer-disabled-actions"
            }
            data-tooltip-content={
              isSaveDraftAllowed ? undefined : disabledFooterTooltip
            }
          >
            <Styled.SecondaryActionButton
              type="button"
              disabled={!isSaveDraftAllowed}
              onClick={handleSaveAsDraft}
            >
              Save as Draft
            </Styled.SecondaryActionButton>
          </Styled.TooltipAnchor>
          <Styled.TooltipAnchor
            data-tooltip-id={
              isCompleteAllowed ? undefined : "roi-footer-disabled-actions"
            }
            data-tooltip-content={
              isCompleteAllowed ? undefined : disabledFooterTooltip
            }
          >
            <Styled.CompleteActionButton
              type="button"
              disabled={!isCompleteAllowed}
              onClick={handleCompleteRequest}
            >
              Complete Request
            </Styled.CompleteActionButton>
          </Styled.TooltipAnchor>
        </Styled.FooterActions>
      </Styled.Footer>
      <Styled.TooltipTheme>
        <Tooltip
          id="roi-footer-disabled-actions"
          place="top"
          opacity={1}
          className="roi-footer-tooltip"
        />
      </Styled.TooltipTheme>
    </Styled.StepScreen>
  );
};
